import { GenerationGuardProvider } from '@/contexts/generation-guard-context';
import { useAddToConversation, useGetPosts } from '@/hooks/queries/practices';
import useGetPromptById from '@/hooks/queries/prompts/useGetPromptById';
import useGetPrompts from '@/hooks/queries/prompts/useGetPrompts';
import { useRunActionState } from '@/hooks/useRunActionState';
import { useSoulMapSessionKeepalive } from '@/hooks/useSoulMapSessionKeepalive';
import ChatContainer from '@/pages/chat-page/components/chat-container';
import { useAuthStore } from '@/store/use-auth-store';
import { useChatStore } from '@/store/useChatStore';
import { useEffect } from 'react';

interface SoulMapChatSessionProps {
  practiceId: string;
  actionTitle: string;
  onLeaveSession: () => void;
  onRestartSession: (actionTitle: string) => void;
}

export function SoulMapChatSession({
  practiceId,
  actionTitle,
  onLeaveSession,
  onRestartSession,
}: SoulMapChatSessionProps) {
  const { setRunActionState } = useRunActionState();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { data: prompts, isLoading: fetchingPrompts } = useGetPrompts({
    enabled: isAuthenticated,
  });
  const { setSelectedPrompt, selectedPrompt } = useChatStore();
  const selectedPromptId =
    selectedPrompt?.id != null ? String(selectedPrompt.id) : undefined;
  const { data: prompt, refetch: refetchPrompt } = useGetPromptById(
    selectedPromptId,
    true
  );
  const { refetch: refetchPosts } = useGetPosts(Number(practiceId));

  useSoulMapSessionKeepalive();

  const addToConversationMutation = useAddToConversation({
    onSuccess: () => {
      void refetchPosts();
    },
  });

  useEffect(() => {
    setRunActionState({ id: practiceId, actionTitle });
  }, [practiceId, actionTitle, setRunActionState]);

  useEffect(() => {
    const matchedPrompt = prompts?.results?.find(
      (entry) => entry.name?.toLowerCase() === actionTitle.toLowerCase()
    );
    if (matchedPrompt) {
      setSelectedPrompt(matchedPrompt);
    }
  }, [prompts, actionTitle, setSelectedPrompt]);

  useEffect(() => {
    if (prompt) {
      setSelectedPrompt(prompt);
    }
  }, [prompt, setSelectedPrompt]);

  useEffect(() => {
    if (selectedPromptId) {
      void refetchPrompt();
    }
  }, [selectedPromptId, refetchPrompt]);

  return (
    <div className="sm-session-shell">
      <div className="sm-session-chat">
        <GenerationGuardProvider>
          <div className="mx-auto flex min-h-0 flex-1 flex-col w-full max-w-full overflow-hidden">
            <ChatContainer
              fetchingPrompts={fetchingPrompts}
              runActionState={{ id: practiceId, actionTitle }}
              fetchingLatetConversation={addToConversationMutation.isPending}
              restartPractice={onRestartSession}
              selectedPrompt={selectedPrompt}
              onLeaveChat={onLeaveSession}
              showRestartButton={false}
              showProfileConfirmation={false}
            />
          </div>
        </GenerationGuardProvider>
      </div>
    </div>
  );
}
